import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tag,
} from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateTime } from "../../../../utils/DateTime";
import { SyncRunRecord } from "../services/model/SyncRunRecord";
import { useGetSyncRunRecordsApiService } from "../services/api/GetSyncRunRecordsApiService";
import { BLOCK_STATUS_TAG } from "../constants";

interface EmployeeBlockEventsTableProps {
  startDate: string;
  endDate: string;
}

type RecordStatusFilter = "ALL" | "Blocked" | "Unblocked";

const RECORD_STATUS_LABELS: Record<RecordStatusFilter, string> = {
  ALL: "All statuses",
  Blocked: "Blocked",
  Unblocked: "Unblocked",
};

const DEFAULT_PAGE_SIZE = 10;

const SKELETON_ROWS = Array.from({ length: DEFAULT_PAGE_SIZE }, (_, i) => ({
  id: String(i),
  employee_name: "",
  employee_no: "",
  status: "Blocked" as const,
  detail: "Blocked" as const,
  reason: null,
  date_created: "",
}));

interface BlockEventsPaginationProps {
  page: number;
  totalPages: number;
  totalCount: number;
  isLoading: boolean;
  onPrev: () => void;
  onNext: () => void;
}

const BlockEventsPagination: React.FC<BlockEventsPaginationProps> = ({
  page,
  totalPages,
  totalCount,
  isLoading,
  onPrev,
  onNext,
}) => {
  const rangeStart = (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * DEFAULT_PAGE_SIZE, totalCount);

  return (
    <div className="flex items-center justify-between mt-sm">
      <span className="text-sm text-greyscale-text-caption">
        {totalCount > 0 ? `${rangeStart}–${rangeEnd} of ${totalCount}` : "0 results"}
      </span>

      {totalPages > 1 && (
        <div className="flex items-center gap-xs">
          <Button
            variant="outline"
            size="sm"
            onClick={onPrev}
            disabled={isLoading || page === 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </Button>
          <span className="text-sm text-greyscale-text-caption px-xs">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={isLoading || page === totalPages}
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
};

type SortCol = "employee_name" | "employee_no" | "status" | "date_created";

enum SortDir {
  Asc = "asc",
  Desc = "desc",
  None = "none",
}

const getSortIcon = (dir: SortDir): JSX.Element => {
  if (dir === SortDir.Asc) return <ArrowUp size={14} />;
  if (dir === SortDir.Desc) return <ArrowDown size={14} />;
  return <ArrowUpDown size={14} />;
};

const SortHeader = ({
  colKey,
  title,
  sortCol,
  sortDir,
  onSortChange,
}: {
  colKey: SortCol;
  title: string;
  sortCol: SortCol;
  sortDir: SortDir;
  onSortChange: (col: SortCol, dir: SortDir) => void;
}): JSX.Element => {
  const isSorted = sortCol === colKey;
  const nextDir = isSorted && sortDir === SortDir.Asc ? SortDir.Desc : SortDir.Asc;
  return (
    <button
      type="button"
      onClick={() => onSortChange(colKey, nextDir)}
      className="flex items-center gap-xxs text-xs font-semibold text-greyscale-text-caption hover:text-greyscale-text-title"
    >
      {title}
      {getSortIcon(isSorted ? sortDir : SortDir.None)}
    </button>
  );
};

const makeRecordColumns = (
  isLoading: boolean,
  sortCol: SortCol,
  sortDir: SortDir,
  onSortChange: (col: SortCol, dir: SortDir) => void
): ColumnDef<SyncRunRecord>[] => [
  {
    accessorKey: "employee_name",
    enableSorting: false,
    header: () => <SortHeader colKey="employee_name" title="Employee" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.employee_name}
        </span>
      ),
  },
  {
    accessorKey: "employee_no",
    enableSorting: false,
    header: () => <SortHeader colKey="employee_no" title="Employee No" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-20" />
      ) : (
        <span className="text-sm font-mono text-greyscale-text-caption">
          {row.original.employee_no}
        </span>
      ),
  },
  {
    accessorKey: "status",
    enableSorting: false,
    header: () => <SortHeader colKey="status" title="Status" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />,
    cell: ({ row }) => {
      if (isLoading) return <Skeleton className="h-5 w-20" />;
      const meta = BLOCK_STATUS_TAG[row.original.status];
      if (!meta)
        return (
          <span className="text-sm text-greyscale-text-caption">{row.original.status}</span>
        );
      return (
        <Tag variant={meta.variant} size="sm">
          {meta.label}
        </Tag>
      );
    },
  },
  {
    accessorKey: "detail",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Detail</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <span className="text-sm text-greyscale-text-body">{row.original.detail}</span>
      ),
  },
  {
    accessorKey: "reason",
    enableSorting: false,
    header: () => <span className="text-xs font-semibold text-greyscale-text-caption">Reason</span>,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <span className="text-sm text-greyscale-text-body">
          {row.original.reason ?? "—"}
        </span>
      ),
  },
  {
    accessorKey: "date_created",
    enableSorting: false,
    header: () => <SortHeader colKey="date_created" title="Date" sortCol={sortCol} sortDir={sortDir} onSortChange={onSortChange} />,
    cell: ({ row }) =>
      isLoading ? (
        <Skeleton className="h-4 w-28" />
      ) : (
        <span className="text-sm text-greyscale-text-body">
          {formatDateTime(row.original.date_created, "en-ZA", {
            dateStyle: "medium",
            timeStyle: "short",
          }) || "—"}
        </span>
      ),
  },
];

const EmployeeBlockEventsTable: React.FC<EmployeeBlockEventsTableProps> = ({
  startDate,
  endDate,
}) => {
  const [statusFilter, setStatusFilter] = useState<RecordStatusFilter>("ALL");
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState<SortCol>("date_created");
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.Desc);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, statusFilter]);

  const handleSortChange = (col: SortCol, dir: SortDir): void => {
    setSortCol(col);
    setSortDir(dir);
    setPage(1);
  };

  const { data, isLoading, isFetching, isError } = useGetSyncRunRecordsApiService(
    {
      start_date: startDate,
      end_date: endDate,
      status: statusFilter === "ALL" ? undefined : statusFilter,
      sort_by: `${sortDir === SortDir.Desc ? "-" : ""}${sortCol}`,
      page,
      page_size: DEFAULT_PAGE_SIZE,
    },
    true
  );

  const records = data?.results ?? [];
  const isTableLoading = isLoading || isFetching;
  const totalPages = data?.pages ?? 1;
  const totalCount = data?.count ?? 0;
  const columns = useMemo(
    () => makeRecordColumns(isTableLoading, sortCol, sortDir, handleSortChange),
    [isTableLoading, sortCol, sortDir, handleSortChange]
  );

  if (isError && !isTableLoading) {
    return (
      <Alert variant="destructive" className="mt-lg">
        <AlertDescription>
          Failed to load employee block events. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`[&_table]:table-fixed [&_input]:h-[46px]${!isTableLoading && records.length === 0 ? " [&_td[colspan]]:invisible" : ""}`}>
      <DataTable
        columns={columns}
        data={isTableLoading ? SKELETON_ROWS : records}
        showPagination={false}
        filterColumn="employee_name"
        filterPlaceholder="Search employee"
        toolbarChildren={
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as RecordStatusFilter)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(RECORD_STATUS_LABELS) as RecordStatusFilter[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {RECORD_STATUS_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {!isTableLoading && records.length === 0 && (
        <div className="-mt-24 flex h-24 items-center justify-center text-sm text-greyscale-text-caption">
          No access status changes during the selected period
        </div>
      )}

      <BlockEventsPagination
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        isLoading={isTableLoading}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    </div>
  );
};

export default EmployeeBlockEventsTable;
